import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";
import { useCreateInputDiscard } from "../../hooks/useInputDiscard";
import { INPUT_DISCARD_REASONS } from "../../services/inputDiscardApi";
import type { InputBatch } from "../../services/inputBatchesApi";

interface RequestInputDiscardDialogProps {
  batch: InputBatch;
  inputName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RequestInputDiscardDialog = ({
  batch,
  inputName,
  open,
  onOpenChange,
  onSuccess,
}: RequestInputDiscardDialogProps) => {
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("Expired");
  const [reasonDetails, setReasonDetails] = useState("");

  const createMutation = useCreateInputDiscard();
  const notify = useNotification();

  const maxQty = batch.currentQuantity;

  const handleClose = () => {
    setQuantity("");
    setReason("Expired");
    setReasonDetails("");
    onOpenChange(false);
  };

  const handleSubmit = () => {
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      notify.error("Enter a quantity greater than zero");
      return;
    }
    if (qty > maxQty) {
      notify.error(`Cannot exceed available quantity (${maxQty})`);
      return;
    }
    if (!reasonDetails.trim()) {
      notify.error("Please provide details for the discard reason");
      return;
    }

    createMutation.mutate(
      {
        inputBatchId: batch.id,
        quantityToDiscard: qty,
        reason,
        reasonDetails: reasonDetails.trim(),
        requiresApproval: true,
      },
      {
        onSuccess: () => {
          notify.success("Discard request submitted — pending manager approval");
          handleClose();
          onSuccess();
        },
        onError: (err: any) => {
          notify.error(err?.response?.data?.message ?? "Failed to submit discard request");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Request Input Discard</DialogTitle>
          <DialogDescription>
            {inputName ? `${inputName} — ` : ""}Batch {batch.batchNumber}
            {" "}· Available: {maxQty} {batch.unitOfMeasure}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel htmlFor="qty">
              Quantity to Discard <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              id="qty"
              type="number"
              step="0.01"
              min="0.01"
              max={maxQty}
              placeholder={`Max ${maxQty}`}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={createMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="reason">
              Reason <span className="text-destructive">*</span>
            </FieldLabel>
            <Select
              value={reason}
              onValueChange={setReason}
              disabled={createMutation.isPending}
            >
              <SelectTrigger id="reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INPUT_DISCARD_REASONS.map((r: { value: string; label: string }) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="details">
              Details <span className="text-destructive">*</span>
            </FieldLabel>
            <Textarea
              id="details"
              rows={3}
              placeholder="Describe the condition, e.g. container compromised, visible mould"
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              disabled={createMutation.isPending}
            />
          </Field>

          <p className="text-xs text-muted-foreground">
            This request will be sent to the Store Keeper / Farm Manager for approval before stock is deducted.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={createMutation.isPending || !quantity || !reasonDetails.trim()}
          >
            {createMutation.isPending ? "Submitting..." : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
